import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../api';
import { TabRecordatorios, sr } from './TabRecordatorios';

const SEGMENTOS = [
  { key: 'todos',       label: 'Todos',        color: '#4cc9f0' },
  { key: 'vigente',     label: '🟢 0-30 días', color: '#4caf50' },
  { key: 'vencido_30',  label: '🟡 31-60 días', color: '#ffc107' },
  { key: 'vencido_60',  label: '🟠 61-90 días', color: '#ff9800' },
  { key: 'critico',     label: '🔴 91-365 días', color: '#e63946' },
  { key: 'incobrable',  label: '💀 +365 días',  color: '#6d1a1a' },
];

const fmt = (n) => n?.toLocaleString('es-EC', { minimumFractionDigits: 2 }) || '0.00';

const TooltipTendencia = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: 6 }}>{d?.label}</div>
      <div style={{ color: '#4cc9f0' }}>Facturado: <strong>${fmt(d?.facturado)}</strong></div>
      <div style={{ color: '#4caf50' }}>Cobrado: <strong>${fmt(d?.cobrado)}</strong></div>
      <div style={{ color: '#e63946' }}>Pendiente: <strong>${fmt(d?.pendiente)}</strong></div>
      <div style={{ color: '#aaa', marginTop: 4 }}>{d?.num_facturas} facturas</div>
    </div>
  );
};

export default function Creditos() {
  const [tab, setTab]               = useState('resumen');
  const [kpis, setKpis]             = useState(null);
  const [clientes, setClientes]     = useState(null);
  const [vencidos, setVencidos]     = useState(null);
  const [cargando, setCargando]     = useState(false);
  const [segmento, setSegmento]     = useState('todos');
  const [busq, setBusq]             = useState('');
  const [drawer, setDrawer]         = useState(null);   // {ruc, nombre}
  const [drawerData, setDrawerData] = useState(null);
  const [cargandoDrawer, setCargandoDrawer] = useState(false);
  const [filtroVenc, setFiltroVenc]   = useState('todos');
  const [revision, setRevision]       = useState(null);
  const [cargandoRev, setCargandoRev] = useState(false);
  const [recConfig, setRecConfig]       = useState(null);
  const [recWhitelist, setRecWhitelist] = useState(null);
  const [recHistorial, setRecHistorial] = useState(null);
  const [recPreview, setRecPreview]     = useState(null);
  const [recCargando, setRecCargando]   = useState(false);
  const [modalConfig, setModalConfig]   = useState(false);
  const [modalWL, setModalWL]           = useState(false);

  useEffect(() => { cargarKpis(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'clientes' && !clientes) cargarClientes(); }, [tab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'vencidos'  && !vencidos)  cargarVencidos();  }, [tab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'revision'  && !revision)  cargarRevision();  }, [tab]);
  useEffect(() => { if (tab === 'recordatorios') cargarRecordatorios();  }, [tab]);

  const cargarKpis = async () => {
    setCargando(true);
    try {
      const data = await api.get('/creditos/kpis');
      setKpis(data.error ? null : data);
    } catch(e) { console.error(e); }
    finally { setCargando(false); }
  };

  const cargarClientes = async () => {
    setCargando(true);
    try {
      const data = await api.get('/creditos/clientes');
      setClientes(data.error ? null : data.clientes);
    } catch(e) { console.error(e); }
    finally { setCargando(false); }
  };

  const cargarRevision = async () => {
    setCargandoRev(true);
    try {
      const data = await api.get('/creditos/revision-diaria');
      setRevision(data.error ? null : data);
    } catch(e) { console.error(e); }
    finally { setCargandoRev(false); }
  };

  const cargarVencidos = async () => {
    setCargando(true);
    try {
      const data = await api.get('/creditos/vencidos');
      setVencidos(data.error ? null : data);
    } catch(e) { console.error(e); }
    finally { setCargando(false); }
  };

  const abrirDrawer = async (ruc, nombre) => {
    setDrawer({ ruc, nombre });
    setDrawerData(null);
    setCargandoDrawer(true);
    try {
      const data = await api.get(`/creditos/cliente/${ruc}`);
      setDrawerData(data.error ? null : data);
    } catch(e) { console.error(e); }
    finally { setCargandoDrawer(false); }
  };

  const clientesFiltrados = (clientes || []).filter(c => {
    const matchSeg  = segmento === 'todos' || c.segmento === segmento;
    const matchBusq = !busq || c.nombre.toUpperCase().includes(busq.toUpperCase()) || c.ruc.includes(busq);
    return matchSeg && matchBusq;
  });

  const vencidosFiltrados = (vencidos?.facturas || []).filter(f =>
    filtroVenc === 'todos' || f.segmento === filtroVenc
  );

  const cargarRecordatorios = async () => {
    setRecCargando(true);
    try {
      const [cfg, wl, hist, prev] = await Promise.all([
        api.get('/recordatorios/config'),
        api.get('/recordatorios/whitelist'),
        api.get('/recordatorios/historial'),
        api.get('/recordatorios/preview'),
      ]);
      setRecConfig(cfg.error   ? null : cfg);
      setRecWhitelist(wl.error ? [] : wl.whitelist);
      setRecHistorial(hist.error ? [] : hist.historial);
      setRecPreview(prev.error ? null : prev);
    } catch(e) { console.error(e); }
    finally { setRecCargando(false); }
  };

  const toggleActivo = async () => {
    await api.patch('/recordatorios/config/toggle', {});
    cargarRecordatorios();
  };

  const ejecutarAhora = async () => {
    if (!window.confirm('¿Enviar recordatorios ahora?')) return;
    setRecCargando(true);
    try {
      const res = await api.post('/recordatorios/ejecutar', {});
      alert(res.error ? `Error: ${res.error}` : `✅ ${res.enviados} enviado(s) · ${res.errores} error(es)`);
      cargarRecordatorios();
    } catch(e) { alert('Error de conexión'); }
    finally { setRecCargando(false); }
  };

  const eliminarWhitelist = async (id) => {
    await api.delete(`/recordatorios/whitelist/${id}`);
    cargarRecordatorios();
  };

  return (
    <div>
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <h2 style={s.titulo}>💳 Créditos</h2>
          <p style={s.subtitulo}>Cuentas por cobrar · cartera activa · vencimientos</p>
        </div>
        <button style={s.botonRefresh} onClick={() => {
          setKpis(null); setClientes(null); setVencidos(null);
          cargarKpis();
          if (tab === 'clientes') cargarClientes();
          if (tab === 'vencidos') cargarVencidos();
          if (tab === 'revision') { setRevision(null); cargarRevision(); }
        }}>🔄 Actualizar</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[
          { key: 'resumen',  label: '📊 Resumen'       },
          { key: 'clientes', label: '👥 Por Cliente'    },
          { key: 'vencidos',  label: '⚠️ Vencidos'        },
          { key: 'revision',  label: '🔍 Revisión Diaria'  },
          { key: 'recordatorios', label: '🔔 Recordatorios' },
        ].map(t => (
          <button key={t.key}
            style={{
              padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
              background: tab === t.key ? '#1a2a3e' : 'none',
              border: tab === t.key ? '1px solid #4cc9f0' : '1px solid #2d2d44',
              color: tab === t.key ? '#4cc9f0' : '#888',
            }}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ TAB RESUMEN ══ */}
      {tab === 'resumen' && (
        <>
          {cargando || !kpis ? (
            <p style={s.cargando}>{cargando ? 'Cargando...' : 'Error cargando datos'}</p>
          ) : (
            <>
              {/* KPIs */}
              <div style={s.kpisRow}>
                {[
                  ['Cartera Total',   `$${fmt(kpis.cartera_total)}`, '#4cc9f0', '#7209b7'],
                  ['Clientes',        kpis.clientes,                 '#fff',    '#4cc9f0'],
                  ['Facturas',        kpis.facturas,                 '#fff',    '#4cc9f0'],
                  ['🟢 Vigente',      `$${fmt(kpis.vigente)}`,       '#4caf50', '#4caf50'],
                  ['🟡 31-60d',       `$${fmt(kpis.vencido_30)}`,    '#ffc107', '#ffc107'],
                  ['🟠 61-90d',       `$${fmt(kpis.vencido_60)}`,    '#ff9800', '#ff9800'],
                  ['🔴 91-365d',      `$${fmt(kpis.critico)}`,       '#e63946', '#e63946'],
                  ['💀 Incobrable',   `$${fmt(kpis.incobrable)}`,    '#6d1a1a', '#6d1a1a'],
                ].map(([label, val, color, border]) => (
                  <div key={label} style={{ ...s.kpiCard, borderTopColor: border }}>
                    <div style={{ ...s.kpiValor, color, fontSize: String(val).length > 8 ? 18 : 22 }}>{val}</div>
                    <div style={s.kpiLabel}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Barra progreso cartera */}
              <div style={{ ...s.card, marginBottom: 24 }}>
                <div style={s.cardHeader}>
                  <span style={s.cardTitulo}>📊 Distribución de cartera</span>
                </div>
                <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
                  {[
                    { val: kpis.vigente,    color: '#4caf50' },
                    { val: kpis.vencido_30, color: '#ffc107' },
                    { val: kpis.vencido_60, color: '#ff9800' },
                    { val: kpis.critico,    color: '#e63946' },
                    { val: kpis.incobrable, color: '#6d1a1a' },
                  ].map((seg, i) => {
                    const pct = kpis.cartera_total > 0 ? (seg.val / kpis.cartera_total) * 100 : 0;
                    return pct > 0 ? (
                      <div key={i} title={`${pct.toFixed(1)}%`}
                        style={{ width: `${pct}%`, background: seg.color, transition: 'width 0.5s' }} />
                    ) : null;
                  })}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  {[
                    ['Vigente',    kpis.vigente,    '#4caf50'],
                    ['31-60d',     kpis.vencido_30, '#ffc107'],
                    ['61-90d',     kpis.vencido_60, '#ff9800'],
                    ['91-365d',    kpis.critico,    '#e63946'],
                    ['Incobrable', kpis.incobrable, '#6d1a1a'],
                  ].map(([label, val, color]) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
                      <span style={{ color: '#aaa', fontSize: 11 }}>{label}: </span>
                      <span style={{ color, fontSize: 11, fontWeight: 'bold' }}>${fmt(val)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gráfico tendencia mensual */}
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <span style={s.cardTitulo}>📈 Créditos vs Cobros — últimos 6 meses</span>
                  <span style={s.cardSub}>
                    <span style={{ color: '#4cc9f0' }}>■</span> Facturado &nbsp;
                    <span style={{ color: '#4caf50' }}>■</span> Cobrado
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={kpis.tendencia} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" vertical={false} />
                    <XAxis dataKey="label" stroke="#555" fontSize={11} tick={{ fill: '#888' }} />
                    <YAxis stroke="#555" fontSize={11} tick={{ fill: '#888' }}
                      tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<TooltipTendencia />} />
                    <Bar dataKey="facturado" fill="#4cc9f0" fillOpacity={0.7} radius={[4,4,0,0]} />
                    <Bar dataKey="cobrado"   fill="#4caf50" fillOpacity={0.9} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </>
      )}

      {/* ══ TAB POR CLIENTE ══ */}
      {tab === 'clientes' && (
        <>
          {/* Filtros */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {SEGMENTOS.map(sg => (
              <button key={sg.key}
                style={{
                  ...s.filtroBtn,
                  ...(segmento === sg.key ? { borderColor: sg.color, color: sg.color, background: '#1a1a2e' } : {}),
                }}
                onClick={() => setSegmento(sg.key)}>
                {sg.label}
              </button>
            ))}
          </div>
          <input
            value={busq} onChange={e => setBusq(e.target.value)}
            placeholder="Buscar por nombre o RUC..."
            style={s.inputBusq}
          />

          {cargando ? <p style={s.cargando}>Cargando clientes...</p> : (
            <div style={s.tabla}>
              <div style={s.tablaHeader}>
                <span style={{ flex: 3 }}>CLIENTE</span>
                <span style={{ width: 70,  textAlign: 'center' }}>FACTS.</span>
                <span style={{ width: 130, textAlign: 'right' }}>FACTURADO</span>
                <span style={{ width: 130, textAlign: 'right' }}>COBRADO</span>
                <span style={{ width: 130, textAlign: 'right' }}>SALDO</span>
                <span style={{ width: 90,  textAlign: 'center' }}>DÍAS MAX</span>
                <span style={{ width: 130, textAlign: 'center' }}>ESTADO</span>
              </div>
              {clientesFiltrados.map((c, i) => (
                <div key={c.ruc}
                  onClick={() => abrirDrawer(c.ruc, c.nombre)}
                  style={{
                    ...s.tablaRow,
                    background: i%2===0 ? '#1e1e2e' : '#1a1a2e',
                    cursor: 'pointer',
                    borderLeft: `3px solid ${c.color}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#2d2d44'}
                  onMouseLeave={e => e.currentTarget.style.background = i%2===0?'#1e1e2e':'#1a1a2e'}
                >
                  <span style={{ flex: 3 }}>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{c.nombre}</div>
                    <div style={{ color: '#555', fontSize: 11 }}>{c.ruc}</div>
                  </span>
                  <span style={{ width: 70, textAlign: 'center', color: '#aaa' }}>{c.num_facturas}</span>
                  <span style={{ width: 130, textAlign: 'right', color: '#aaa', fontSize: 12 }}>${fmt(c.total_facturado)}</span>
                  <span style={{ width: 130, textAlign: 'right', color: '#4caf50', fontSize: 12 }}>${fmt(c.total_cobrado)}</span>
                  <span style={{ width: 130, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold', fontSize: 13 }}>${fmt(c.saldo_pendiente)}</span>
                  <span style={{ width: 90, textAlign: 'center' }}>
                    <span style={{ color: c.color, fontWeight: 'bold', fontSize: 13 }}>{c.dias_max}d</span>
                  </span>
                  <span style={{ width: 130, textAlign: 'center' }}>
                    <span style={{
                      background: c.color + '22', color: c.color,
                      borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 'bold',
                    }}>
                      {SEGMENTOS.find(s => s.key === c.segmento)?.label || c.segmento}
                    </span>
                  </span>
                </div>
              ))}
              {/* Footer */}
              <div style={{ display: 'flex', padding: '10px 16px', background: '#0f0f1a', borderTop: '2px solid #2d2d44', gap: 8 }}>
                <span style={{ flex: 3, color: '#aaa', fontSize: 12 }}>{clientesFiltrados.length} clientes</span>
                <span style={{ width: 70 }} />
                <span style={{ width: 130 }} />
                <span style={{ width: 130, textAlign: 'right', color: '#4caf50', fontWeight: 'bold' }}>
                  ${fmt(clientesFiltrados.reduce((a,c) => a+c.total_cobrado, 0))}
                </span>
                <span style={{ width: 130, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold' }}>
                  ${fmt(clientesFiltrados.reduce((a,c) => a+c.saldo_pendiente, 0))}
                </span>
                <span style={{ width: 220 }} />
              </div>
            </div>
          )}
        </>
      )}

      {/* ══ TAB VENCIDOS ══ */}
      {tab === 'vencidos' && (
        <>
          {/* KPIs vencidos */}
          {vencidos && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              {[
                ['31-60 días',   vencidos.totales.vencido_30, '#ffc107', 'vencido_30'],
                ['61-90 días',   vencidos.totales.vencido_60, '#ff9800', 'vencido_60'],
                ['91-365 días',  vencidos.totales.critico,    '#e63946', 'critico'],
                ['Incobrables',  vencidos.totales.incobrable, '#6d1a1a', 'incobrable'],
              ].map(([label, val, color, key]) => (
                <div key={key}
                  onClick={() => setFiltroVenc(filtroVenc === key ? 'todos' : key)}
                  style={{
                    ...s.kpiCard, borderTopColor: color, cursor: 'pointer',
                    background: filtroVenc === key ? '#1a1a2e' : '#1e1e2e',
                    border: filtroVenc === key ? `1px solid ${color}` : '1px solid transparent',
                  }}>
                  <div style={{ ...s.kpiValor, color, fontSize: 18 }}>${fmt(val)}</div>
                  <div style={s.kpiLabel}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {cargando ? <p style={s.cargando}>Cargando vencidos...</p> : (
            <div style={s.tabla}>
              <div style={s.tablaHeader}>
                <span style={{ width: 95 }}>FECHA</span>
                <span style={{ width: 160 }}>FACTURA</span>
                <span style={{ flex: 3 }}>CLIENTE</span>
                <span style={{ width: 110, textAlign: 'right' }}>TOTAL</span>
                <span style={{ width: 110, textAlign: 'right' }}>SALDO</span>
                <span style={{ width: 90,  textAlign: 'center' }}>DÍAS</span>
                <span style={{ width: 120, textAlign: 'center' }}>ESTADO</span>
              </div>
              <div style={{ maxHeight: 'calc(100vh - 380px)', overflowY: 'auto' }}>
                {vencidosFiltrados.map((f, i) => (
                  <div key={f.factura} style={{
                    ...s.tablaRow,
                    background: i%2===0 ? '#1e1e2e' : '#1a1a2e',
                    borderLeft: `3px solid ${f.color}`,
                    cursor: 'pointer',
                  }}
                  onClick={() => abrirDrawer(f.ruc, f.cliente)}>
                    <span style={{ width: 95, color: '#aaa', fontSize: 11 }}>{f.fecha}</span>
                    <span style={{ width: 160, color: '#4cc9f0', fontSize: 11, fontFamily: 'monospace' }}>{f.factura}</span>
                    <span style={{ flex: 3 }}>
                      <div style={{ color: '#fff', fontSize: 12 }}>{f.cliente}</div>
                      <div style={{ color: '#555', fontSize: 10 }}>{f.ruc}</div>
                    </span>
                    <span style={{ width: 110, textAlign: 'right', color: '#aaa', fontSize: 12 }}>${fmt(f.total)}</span>
                    <span style={{ width: 110, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold', fontSize: 13 }}>${fmt(f.saldo)}</span>
                    <span style={{ width: 90, textAlign: 'center', color: f.color, fontWeight: 'bold' }}>{f.dias}d</span>
                    <span style={{ width: 120, textAlign: 'center' }}>
                      <span style={{ background: f.color+'22', color: f.color, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 'bold' }}>
                        {f.segmento === 'incobrable' ? '💀 Incobrable' :
                         f.segmento === 'critico'    ? '🔴 Crítico' :
                         f.segmento === 'vencido_60' ? '🟠 +60d' : '🟡 +30d'}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ padding: '10px 16px', background: '#0f0f1a', borderTop: '2px solid #2d2d44' }}>
                <span style={{ color: '#aaa', fontSize: 12 }}>
                  {vencidosFiltrados.length} facturas · Saldo total: <strong style={{ color: '#e63946' }}>
                    ${fmt(vencidosFiltrados.reduce((a,f) => a+f.saldo, 0))}
                  </strong>
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'recordatorios' && (
       <TabRecordatorios
         recCargando={recCargando}
         recConfig={recConfig}
         recWhitelist={recWhitelist}
         recHistorial={recHistorial}
         recPreview={recPreview}
         toggleActivo={toggleActivo}
         ejecutarAhora={ejecutarAhora}
         eliminarWhitelist={eliminarWhitelist}
         modalConfig={modalConfig}
         setModalConfig={setModalConfig}
         modalWL={modalWL}
         setModalWL={setModalWL}
         cargarRecordatorios={cargarRecordatorios}
       />
     )}
     

      {/* ══ TAB REVISIÓN DIARIA ══ */}
      {tab === 'revision' && (
        <>
          {cargandoRev ? (
            <p style={s.cargando}>Cargando revisión...</p>
          ) : !revision ? (
            <p style={{ color: '#e63946', padding: 20 }}>Error cargando datos</p>
          ) : (
            <>
              {/* Header fecha + KPIs alertas */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ color: '#aaa', fontSize: 13 }}>
                  Créditos emitidos el <strong style={{ color: '#fff' }}>{revision.fecha}</strong>
                  {' · '}{revision.total} facturas
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    ['🟢 Sin deuda',   revision.alertas.verde,    '#4caf50'],
                    ['🟡 Por vencer',  revision.alertas.amarillo, '#ffc107'],
                    ['🔴 Vencida +90d',revision.alertas.rojo,     '#e63946'],
                  ].map(([label, count, color]) => (
                    <div key={label} style={{ background: '#1e1e2e', border: `1px solid ${color}33`, borderRadius: 8, padding: '6px 14px', textAlign: 'center' }}>
                      <div style={{ color, fontWeight: 'bold', fontSize: 16 }}>{count}</div>
                      <div style={{ color: '#555', fontSize: 10 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabla */}
              <div style={s.tabla}>
                <div style={s.tablaHeader}>
                  <span style={{ width: 140 }}>FECHA</span>
                  <span style={{ width: 160 }}>FACTURA</span>
                  <span style={{ flex: 3 }}>CLIENTE</span>
                  <span style={{ width: 110, textAlign: 'right' }}>TOTAL</span>
                  <span style={{ width: 110, textAlign: 'right' }}>SALDO</span>
                  <span style={{ width: 130, textAlign: 'right' }}>DEUDA PREVIA</span>
                  <span style={{ width: 90,  textAlign: 'center' }}>ESTADO</span>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
                  {revision.facturas.map((f, i) => (
                    <div key={f.factura}
                      onClick={() => abrirDrawer(f.ruc, f.cliente)}
                      style={{
                        ...s.tablaRow,
                        background: i%2===0 ? '#1e1e2e' : '#1a1a2e',
                        borderLeft: `3px solid ${f.color}`,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#2d2d44'}
                      onMouseLeave={e => e.currentTarget.style.background = i%2===0?'#1e1e2e':'#1a1a2e'}>
                      <span style={{ width: 140, color: '#aaa', fontSize: 11 }}>{f.fecha}</span>
                      <span style={{ width: 160, color: '#4cc9f0', fontSize: 11, fontFamily: 'monospace' }}>{f.factura}</span>
                      <span style={{ flex: 3 }}>
                        <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{f.cliente}</div>
                        <div style={{ color: '#555', fontSize: 11 }}>{f.ruc}</div>
                      </span>
                      <span style={{ width: 110, textAlign: 'right', color: '#aaa', fontSize: 12 }}>${fmt(f.total)}</span>
                      <span style={{ width: 110, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold', fontSize: 12 }}>${fmt(f.saldo)}</span>
                      <span style={{ width: 130, textAlign: 'right' }}>
                        {f.cartera_previa > 0 ? (
                          <div>
                            <div style={{ color: f.color, fontWeight: 'bold', fontSize: 12 }}>${fmt(f.cartera_previa)}</div>
                            {f.facturas_previas > 0 && (
                              <div style={{ color: '#555', fontSize: 10 }}>{f.facturas_previas} fact · {f.dias_max_previo}d máx</div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#4caf50', fontSize: 11 }}>✓ Limpio</span>
                        )}
                      </span>
                      <span style={{ width: 90, textAlign: 'center' }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%',
                          background: f.color, margin: '0 auto',
                          boxShadow: `0 0 6px ${f.color}`,
                        }} />
                      </span>
                    </div>
                  ))}
                </div>
                {revision.total === 0 && (
                  <p style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>
                    No se emitieron créditos ayer
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ══ DRAWER CLIENTE ══ */}
      {drawer && (
        <>
          <div onClick={() => setDrawer(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100 }} />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
            background: '#1e1e2e', zIndex: 101, display: 'flex', flexDirection: 'column',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
          }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #2d2d44', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: 15 }}>💳 Detalle de créditos</div>
                <div style={{ color: '#fff', fontSize: 14, marginTop: 4 }}>{drawer.nombre}</div>
                <div style={{ color: '#555', fontSize: 12 }}>{drawer.ruc}</div>
              </div>
              <button onClick={() => setDrawer(null)}
                style={{ background: 'none', border: 'none', color: '#555', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Contenido */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
              {cargandoDrawer ? (
                <p style={s.cargando}>Cargando facturas...</p>
              ) : !drawerData ? (
                <p style={{ color: '#e63946' }}>Error cargando datos</p>
              ) : (
                <>
                  {/* Resumen */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <div style={{ flex: 1, background: '#0f0f1a', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold' }}>FACTURAS</div>
                      <div style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>{drawerData.total}</div>
                    </div>
                    <div style={{ flex: 1, background: '#0f0f1a', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold' }}>SALDO TOTAL</div>
                      <div style={{ color: '#4cc9f0', fontSize: 18, fontWeight: 'bold' }}>
                        ${fmt(drawerData.facturas.reduce((a,f) => a+f.saldo, 0))}
                      </div>
                    </div>
                    <div style={{ flex: 1, background: '#0f0f1a', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold' }}>DÍAS MÁX.</div>
                      <div style={{ color: drawerData.facturas[drawerData.facturas.length-1]?.color || '#aaa', fontSize: 18, fontWeight: 'bold' }}>
                        {Math.max(...drawerData.facturas.map(f => f.dias))}d
                      </div>
                    </div>
                  </div>

                  {/* Facturas */}
                  {drawerData.facturas.map((f, i) => (
                    <div key={f.factura} style={{
                      background: i%2===0 ? '#0f0f1a' : '#131320',
                      borderRadius: 8, padding: '12px 14px', marginBottom: 8,
                      borderLeft: `3px solid ${f.color}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ color: '#4cc9f0', fontSize: 12, fontFamily: 'monospace' }}>{f.factura}</span>
                        <span style={{ background: f.color+'22', color: f.color, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 'bold' }}>
                          {f.dias}d
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                        {[
                          ['FECHA',    f.fecha,          '#aaa'],
                          ['TOTAL',    `$${fmt(f.total)}`, '#fff'],
                          ['SALDO',    `$${fmt(f.saldo)}`, '#4cc9f0'],
                          ['COBRADO',  `$${fmt(f.cobrado)}`, '#4caf50'],
                          ['% COBRADO', f.total > 0 ? `${((f.cobrado/f.total)*100).toFixed(0)}%` : '0%', '#4caf50'],
                        ].map(([label, val, color]) => (
                          <div key={label}>
                            <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold' }}>{label}</div>
                            <div style={{ color, fontSize: 12, marginTop: 2 }}>{val}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  headerRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  titulo:      { color: '#fff', marginBottom: 4 },
  subtitulo:   { color: '#555', fontSize: 13 },
  botonRefresh:{ background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  cargando:    { color: '#aaa', padding: 40, textAlign: 'center' },
  kpisRow:     { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard:     { background: '#1e1e2e', borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 120, borderTop: '3px solid #7209b7' },
  kpiValor:    { fontSize: 22, fontWeight: 'bold', color: '#fff', marginBottom: 4 },
  kpiLabel:    { color: '#aaa', fontSize: 11 },
  card:        { background: '#1e1e2e', borderRadius: 12, padding: 24, marginBottom: 24 },
  cardHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 },
  cardTitulo:  { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  cardSub:     { color: '#555', fontSize: 11 },
  filtroBtn:   { background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 6, color: '#888', padding: '5px 12px', cursor: 'pointer', fontSize: 11 },
  inputBusq:   { width: '100%', background: '#0f0f1a', border: '1px solid #2d2d44', borderRadius: 8, color: '#fff', padding: '10px 14px', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' },
  tabla:       { background: '#1e1e2e', borderRadius: 12, overflow: 'hidden' },
  tablaHeader: { display: 'flex', padding: '10px 16px', background: '#0f0f1a', color: '#555', fontSize: 11, fontWeight: 'bold', gap: 8 },
  tablaRow:    { display: 'flex', padding: '12px 16px', gap: 8, alignItems: 'center', borderBottom: '1px solid #0f0f1a', transition: 'background 0.15s' },
};